import { Extension, Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table';
import { supportedEmbed, renderableHtml } from './legacyHtml';

const EmbeddedVideo = Node.create({
  name: 'embeddedVideo',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      title: { default: 'Embedded video' },
      width: { default: '560' },
      height: { default: '315' },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'iframe',
        getAttrs: (el) =>
          supportedEmbed(el.getAttribute('src')) ? null : false,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'iframe',
      mergeAttributes(HTMLAttributes, {
        sandbox: 'allow-scripts allow-same-origin allow-presentation',
        allowfullscreen: '',
        loading: 'lazy',
        style: 'max-width:100%;border:0',
      }),
    ];
  },
});

const Layout = Node.create({
  name: 'layout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return {
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-section-label'),
        renderHTML: (attrs) =>
          attrs.label ? { 'data-section-label': attrs.label } : {},
      },
      tag: {
        default: 'section',
        rendered: false,
        parseHTML: (el) => el.tagName.toLowerCase(),
      },
    };
  },
  parseHTML() {
    return [
      { tag: 'section' },
      { tag: 'article' },
      ...[
        'main',
        'header',
        'footer',
        'aside',
        'nav',
        'figure',
        'figcaption',
        'details',
        'summary',
        'dl',
        'dt',
        'dd',
      ].map((tag) => ({ tag })),
      { tag: 'div:not([data-tracking-pixel])' },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [node.attrs.tag, HTMLAttributes, 0];
  },
});
const Marketing = Node.create({
  name: 'marketing',
  group: 'block',
  atom: true,
  addAttributes() {
    return {
      config: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-tracking-pixel'),
        renderHTML: (attrs) => ({ 'data-tracking-pixel': attrs.config }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-tracking-pixel]', priority: 1000 }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes];
  },
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'lt-marketing-marker';
      dom.contentEditable = 'false';
      let label = 'Marketing integration';
      try {
        const c = JSON.parse(node.attrs.config);
        label = `${c.type} · ${c.pixelId}`;
      } catch (_) {
        /* Retain a neutral label for legacy malformed settings. */
      }
      dom.textContent = `◈ ${label} — inactive in editor`;
      return { dom };
    };
  },
});
const mediaNode = (tag) =>
  Node.create({
    name: tag,
    group: 'block',
    atom: true,
    addAttributes() {
      return {
        src: {
          default: null,
          parseHTML: (el) =>
            el.getAttribute('src') ||
            el.querySelector('source')?.getAttribute('src'),
        },
        controls: { default: true },
      };
    },
    parseHTML() {
      return [{ tag }];
    },
    renderHTML({ HTMLAttributes }) {
      return [
        tag,
        mergeAttributes(HTMLAttributes, {
          controls: '',
          style: 'max-width:100%',
        }),
      ];
    },
  });
const StylePreservation = Extension.create({
  name: 'layoutStyles',
  addGlobalAttributes() {
    return [
      {
        types: [
          'layout',
          'paragraph',
          'heading',
          'image',
          'table',
          'tableRow',
          'tableCell',
          'tableHeader',
          'blockquote',
          'bulletList',
          'orderedList',
          'listItem',
        ],
        attributes: {
          style: { default: null, parseHTML: (el) => el.getAttribute('style') },
          class: { default: null },
          id: { default: null },
        },
      },
    ];
  },
});
// Preserve decorative SVGs as isolated graphics while surrounding copy stays
// editable. A diagram must not disable the entire document or vanish on save.
const Graphic = Node.create({
  name: 'graphic',
  group: 'block',
  atom: true,
  addAttributes() {
    return { html: { default: '' } };
  },
  parseHTML() {
    return [
      {
        tag: 'svg',
        getAttrs: (el) => ({ html: renderableHtml(el.outerHTML) }),
      },
    ];
  },
  renderHTML({ node }) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = renderableHtml(node.attrs.html);
    return wrapper.firstElementChild || ['div'];
  },
});
export const composerExtensions = () => [
  StarterKit.configure({
    link: {
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer' },
    },
  }),
  Image.configure({ inline: true, allowBase64: true }),
  TextStyleKit,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  TableKit,
  Layout,
  Marketing,
  EmbeddedVideo,
  mediaNode('video'),
  mediaNode('audio'),
  StylePreservation,
  Graphic,
];

// Keep unsupported legacy documents in source mode instead of flattening them.
export function unsupportedMarkup(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (
    Boolean(
      doc.querySelector('form,input,button,script,style,canvas,object,embed')
    ) ||
    Array.from(doc.querySelectorAll('iframe')).some(
      (frame) => !supportedEmbed(frame.getAttribute('src'))
    )
  );
}
